/**
 * Investing Idea page — pure navigation/composition wrapper consolidating
 * three previously-standalone top-nav items (Market Opportunities,
 * Portfolio Tracker, Stock Data) into one page with an internal tab bar,
 * mirroring `OptionsAnalystPage.tsx`'s own established pattern (mount-once
 * -keep-alive TabPanel, `?tab=` synced to the URL via replace).
 *
 * Renders `MarketOpportunitiesPage`/`PortfolioTrackerPage`/`StockDataPage`
 * completely unmodified as children — no state, calculations, or API calls
 * from any of the three are touched.
 *
 * The one addition (2026-09-14): the 4 Breakout-status summary counters
 * (Strong Breakout / Breakout Candidate / Developing Breakout / No
 * Confirmed Breakout) render right-aligned in this SAME tab row, next to
 * "STOCK DATA". They used to live inside `MarketOpportunitiesPage` itself,
 * populated only as a side effect of whichever Top Breakout/Shift To
 * Breakout/All fetch last ran there — so switching to Breakouts or any
 * Market Cap Segment view showed them stuck at zero. The backend already
 * computes this summary over the WHOLE scored universe regardless of any
 * tab/status filter (`MarketOpportunityQueryService._build_summary`), so
 * it's fetched here independently, once, via a minimal
 * `page=1&page_size=1` call — completely decoupled from
 * `MarketOpportunitiesPage`'s own internal view state.
 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { Box, Paper, Stack, Tab, Tabs, Tooltip, Typography } from "@mui/material";
import MarketOpportunitiesPage from "@/pages/MarketOpportunitiesPage";
import PortfolioTrackerPage from "@/pages/PortfolioTrackerPage";
import StockDataPage from "@/pages/StockDataPage";
import marketOpportunityService from "@/services/marketOpportunityService";
import type { MarketOpportunitySummary } from "@/types";

type InvestingIdeaTab = "market-opportunities" | "portfolio-tracker" | "stock-data";

const INVESTING_IDEA_TABS: { value: InvestingIdeaTab; label: string }[] = [
  { value: "market-opportunities", label: "MARKET OPPORTUNITIES" },
  { value: "portfolio-tracker", label: "PORTFOLIO TRACKER" },
  { value: "stock-data", label: "STOCK DATA" },
];

// Breakout Engine V1 palette, kept as its own small local copy rather than
// imported from `MarketOpportunitiesPage`/`BreakoutBreakdown` — this
// codebase's established convention is per-consumer duplication of this
// 4-color palette rather than a single shared module (see
// `MarketOpportunitiesPage.tsx`'s own identical local copy).
const SUMMARY_STATUS_COLORS = {
  strong_breakout: "#1B5E20",
  breakout_candidate: "#2E7D32",
  developing_breakout: "#F9A825",
  no_confirmed_breakout: "#9E9E9E",
} as const;

const SUMMARY_CARD_DEFS: { key: keyof MarketOpportunitySummary; label: string }[] = [
  { key: "strong_breakout", label: "Strong Breakout" },
  { key: "breakout_candidate", label: "Breakout Candidate" },
  { key: "developing_breakout", label: "Developing Breakout" },
  { key: "no_confirmed_breakout", label: "No Confirmed Breakout" },
];

/**
 * Keeps a tab's content mounted once visited (rather than tearing it down
 * every time it's hidden) purely via CSS — same pattern as
 * `OptionsAnalystPage.tsx`'s own `TabPanel`.
 */
function TabPanel({ active, children }: { active: boolean; children: ReactNode }) {
  return <Box sx={{ display: active ? "block" : "none" }}>{children}</Box>;
}

export function InvestingIdeaPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTab] = useState<InvestingIdeaTab>(() => {
    const fromUrl = searchParams.get("tab");
    return fromUrl === "portfolio-tracker" || fromUrl === "stock-data"
      ? fromUrl
      : "market-opportunities";
  });

  // Every tab visited at least once stays mounted from then on (hidden via
  // `TabPanel`'s CSS, not unmounted).
  const [mountedTabs, setMountedTabs] = useState<Set<InvestingIdeaTab>>(() => new Set([tab]));
  useEffect(() => {
    setMountedTabs((prev) => (prev.has(tab) ? prev : new Set(prev).add(tab)));
  }, [tab]);

  // Mirror the active tab into the URL (merge, replace not push) so a
  // bookmark/reload restores the right tab.
  useEffect(() => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.set("tab", tab);
        return next;
      },
      { replace: true }
    );
  }, [tab, setSearchParams]);

  // Independent summary-counter fetch (see file docstring) — fires once on
  // mount, regardless of which of the 3 top-level tabs (or, once inside
  // Market Opportunities, which of its flat views, including RSI(2)
  // Pullback) is active. `page_size:
  // 1` keeps the response payload small; the backend still computes
  // `summary` over the full universe either way (no cheaper endpoint
  // exists), we just don't need the `items` it also returns.
  const [opportunitySummary, setOpportunitySummary] = useState<MarketOpportunitySummary>({
    strong_breakout: 0,
    breakout_candidate: 0,
    developing_breakout: 0,
    no_confirmed_breakout: 0,
  });
  useEffect(() => {
    let cancelled = false;
    marketOpportunityService
      .list(undefined, { page: 1, page_size: 1 })
      .then((response) => {
        if (!cancelled) setOpportunitySummary(response.summary);
      })
      .catch(() => {
        // Decorative counters only -- fail silently rather than surfacing a
        // page-level error banner for a badge row; they simply stay at 0.
      });
    return () => {
      cancelled = true;
    };
  }, []);

  return (
    <Box>
      <Box sx={{ position: "sticky", top: 0, zIndex: 11, bgcolor: "background.paper" }}>
        <Paper variant="outlined" sx={{ p: 0.75, mb: 0 }}>
          <Box
            sx={{
              display: "flex",
              // UI review (2026-10-03): this used to be `nowrap` from the
              // `md` breakpoint up, which squeezed the Tabs into whatever
              // width was left over AFTER the 4 breakout-status counters on
              // the right claimed theirs -- on an ordinary desktop width
              // (~1280px), that was little enough that "PORTFOLIO TRACKER"/
              // "STOCK DATA" got clipped with no visible scroll affordance.
              // Always wrapping means the Tabs row gets the FULL row width
              // to itself (so it basically never needs to scroll at normal
              // widths), with the counters dropping to their own line below
              // instead of fighting the Tabs for space.
              flexWrap: "wrap",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <Tabs
              value={tab}
              onChange={(_e, value) => setTab(value as InvestingIdeaTab)}
              variant="scrollable"
              scrollButtons="auto"
              // Guarantees the scroll chevrons render even when the browser
              // reports a coarse/touch pointer (MUI hides them there by
              // default) -- belt-and-suspenders on top of the wrap fix
              // above, so these 3 tabs are never strandable off-screen with
              // no way to reach them.
              allowScrollButtonsMobile
              sx={{ minHeight: 44, flexGrow: 1, minWidth: 0 }}
              TabIndicatorProps={{ sx: { height: 3, bgcolor: "#1E3A8A" } }}
            >
              {INVESTING_IDEA_TABS.map((t) => (
                <Tab
                  key={t.value}
                  value={t.value}
                  label={t.label}
                  sx={{
                    minHeight: 44,
                    py: 0.5,
                    fontWeight: tab === t.value ? 700 : 600,
                    color: tab === t.value ? "#0F172A" : "#334155",
                    "&.Mui-selected": { color: "#0F172A" },
                  }}
                />
              ))}
            </Tabs>
            {/* Right-aligned, same row as the tabs (next to "STOCK DATA") --
                see file docstring for why this lives here instead of inside
                MarketOpportunitiesPage. */}
            <Stack
              direction="row"
              flexWrap="wrap"
              alignItems="baseline"
              columnGap={1.5}
              rowGap={0.5}
              sx={{ flexShrink: 0, pr: 1 }}
            >
              {/* UI review (2026-10-04): these counters come from the Breakout
                  Score engine's whole universe, not from whichever view is
                  open (e.g. the Volume + OBV Breakout Scanner), so they are
                  labeled to avoid being read as that view's results. */}
              <Typography component="span" variant="body2" fontWeight={700} sx={{ whiteSpace: "nowrap" }}>
                Breakout Score universe:
              </Typography>
              <Tooltip title="These counters come from the Breakout Score engine. The Volume + OBV Breakout Scanner is a separate screen with its own rules, so its Strong Breakouts are not counted here.">
                <Typography component="span" variant="caption" color="text.disabled" sx={{ cursor: "help" }}>
                  (separate from the Volume + OBV scanner)
                </Typography>
              </Tooltip>
              {SUMMARY_CARD_DEFS.map((card, index) => (
                <Box key={card.key} component="span" sx={{ display: "inline-flex", alignItems: "baseline", gap: 1.5 }}>
                  {index > 0 && (
                    <Typography component="span" variant="body2" color="text.disabled" sx={{ userSelect: "none" }}>
                      |
                    </Typography>
                  )}
                  <Typography component="span" variant="body2" sx={{ whiteSpace: "nowrap", color: "text.secondary" }}>
                    {card.label}:{" "}
                    <Box component="span" sx={{ color: SUMMARY_STATUS_COLORS[card.key], fontWeight: 700 }}>
                      {opportunitySummary[card.key]}
                    </Box>
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Paper>
      </Box>
      {mountedTabs.has("market-opportunities") && (
        <TabPanel active={tab === "market-opportunities"}>
          <MarketOpportunitiesPage />
        </TabPanel>
      )}
      {mountedTabs.has("portfolio-tracker") && (
        <TabPanel active={tab === "portfolio-tracker"}>
          <PortfolioTrackerPage />
        </TabPanel>
      )}
      {mountedTabs.has("stock-data") && (
        <TabPanel active={tab === "stock-data"}>
          <StockDataPage />
        </TabPanel>
      )}
    </Box>
  );
}

export default InvestingIdeaPage;
